<template>
  <div>
    <ts-page-title
      :title="$t('payRollCycle.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('payRollCycle.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper class="ts-flex ts-items-center ts-justify-between">
        <Button type="info" ghost @click="addNew">{{
          $t('addNew')
        }}</Button>
        <div class="ts-justify-end ts-space-x-2">
          <Input
            search
            v-model="search"
            :placeholder="$t('payRollCycle.Searchstatus')"
            :style="width"
            @on-focus="() => (width = 'width: 500px')"
            @on-blur="() => (width = 'width: 300px')"
          />
        </div>
      </ts-panel-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        highlight-row
        stripe
        max-height="700"
      >
        <template slot-scope="{ row }" slot="released_by">
          {{
            row.released_by
              ? row.realease_by_user.employee
                ? row.realease_by_user.employee.employee_name_en
                : row.realease_by_user.user_name
              : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="is_released">
          <span>
            <span
              v-if="row.is_released"
              class="ios-checkmark-circle-outline badge bg-success"
              >Yes</span
            >
            <span v-else class="ios-close-circle-outline badge bg-danger"
              >NO</span
            >
          </span>
        </template>
        <template slot-scope="{ row }" slot="is_notification_sent">
          <span>
            <span
              v-if="row.is_notification_sent"
              class="ios-checkmark-circle-outline badge bg-success"
              >Yes</span
            >
            <span v-else class="ios-close-circle-outline badge bg-danger"
              >NO</span
            >
          </span>
        </template>
        <template slot-scope="{ row }" slot="salary_tax_fx_rate">
          <strong>{{ formatNumber(row.salary_tax_fx_rate) }}</strong>
        </template>
        <template slot-scope="{ row }" slot="nssf_fx_rate">
          <strong>{{ formatNumber(row.nssf_fx_rate) }}</strong>
        </template>
        <template slot-scope="{ row }" slot="action">
          <a
            href="#"
            class="text-primary"
            @click.prevent="onEdit(row)"
            v-tooltip="$t('edit')"
          >
            <Icon type="ios-create" size="20" />
          </a>
          <Poptip
            confirm
            :title="$t('areYouSure')"
            @on-ok="onRelease(row)"
            :transfer="true"
            :ok-text="$t('yes')"
            :cancel-text="$t('no')"
          >
            <a
              class="text-danger"
              v-tooltip="$t('payRollCycle.release')"
              :disabled="row._release"
            >
              <i class="fas fa-circle-notch fa-spin" v-if="row._release"></i>
              <i class="far fa-registered ts-p-2" size="40" v-else></i>
            </a>
          </Poptip>
        </template>
      </Table>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
      <a-modal
        v-model="showForm"
        draggable
        sticky
        scrollable
        :mask="false"
        :footer="null"
        :z-index="1022"
        :title="$t('payRollCycle.pageTitle')"
      >
        <from-action
          ref="form_action"
          @cancel="clearEdit"
          @fetchData="fetchData"
        />
      </a-modal>
    </ts-panel>
  </div>
</template>
<script>
import FromAction from './form.vue'
import { mapState, mapGetters } from 'vuex'
import { debounce } from 'lodash'

export default {
  name: 'payroll-cycle',
  components: {
    FromAction
  },
  data() {
    return {
      loading: false,
      showForm: false,
      width: 'width: 300px'
    }
  },
  computed: {
    ...mapGetters(['formatNumber']),
    ...mapState('payroll/payrollCycle', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.payroll.payrollCycle.search
      },
      set(newValue) {
        this.$store.commit('payroll/payrollCycle/SET_SEARCH', newValue)
        this.$store.commit('payroll/payrollCycle/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          title: this.$t('payRollCycle.cycleYear'),
          key: 'cycle_year',
          sortable: true,
          minWidth: 150
        },
        {
          title: this.$t('payRollCycle.cycleMonth'),
          key: 'cycle_month_display',
          sortable: true,
          minWidth: 150
        },
        {
          title: this.$t('payRollCycle.status'),
          key: 'cycle_status',
          sortable: true,
          minWidth: 180
        },
        {
          title: this.$t('payRollCycle.release'),
          key: 'is_released',
          slot: 'is_released',
          sortable: true,
          minWidth: 180,
          align: 'center'
        },
        {
          title: this.$t('payRollCycle.releasDate'),
          key: 'released_date',
          sortable: true,
          minWidth: 180,
          align: 'center'
        },
        {
          title: this.$t('payRollCycle.releasBy'),
          slot: 'released_by',
          sortable: true,
          minWidth: 180,
          align: 'center'
        },
        {
          title: this.$t('payRollCycle.notification'),
          key: 'is_notification_sent',
          slot: 'is_notification_sent',
          sortable: true,
          minWidth: 180,
          align: 'center'
        },
        {
          title: this.$t('payRollCycle.dateSend'),
          key: 'date_sent',
          sortable: true,
          minWidth: 180
        },
        {
          title: this.$t('payRollCycle.salaryTaxFxRate'),
          slot: 'salary_tax_fx_rate',
          sortable: true,
          align: 'center',
          minWidth: 180
        },
        {
          title: this.$t('payRollCycle.nssfFxRate'),
          slot: 'nssf_fx_rate',
          sortable: true,
          align: 'center',
          minWidth: 180
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 95
        }
      ]
    }
  },
  methods: {
    addNew() {
      this.$store.commit('payroll/payrollCycle/SET_EDIT_DATA', {})
      this.showForm = true
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('payroll/payrollCycle/fetch', attributes)
        .catch(error => {
          this.loading = false
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async onRelease(record) {
      record._release = true
      this.$store
        .dispatch('payroll/payrollCycle/destroy', record.payroll_cycle_id)
        .then(response => {
          this.fetchData()
          record._release = false
          this.notice({
            type: response.message_id == 0 ? 'success' : 'warning',
            text: response.message
          })
        })
        .catch(error => {
          this.notice({ type: 'error', text: error.message })
          record._release = false
        })
    },
    onEdit(record) {
      this.showForm = true
      this.$store.commit('payroll/payrollCycle/SET_EDIT_DATA', record)
      this.$nextTick(() => {
        this.$refs.form_action.setEditData()
      })
    },
    clearEdit() {
      this.showForm = false
      this.$refs.form_action.clearInput()
      this.$refs.form_action.clearInput()
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'PAYROLL CYCLE',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  }
}
</script>
